// src/components/Lms/Pages/TiendaPremios.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    FiAward,
    FiShoppingCart,
    FiCheck,
    FiClock,
    FiDollarSign,
    FiGift,
    FiSearch,
} from "react-icons/fi";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { API_BASE } from "../../../api";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";
import AlertDialog from "../Components/Shared/AlertDialog";
import ConfirmDialog from "../Components/Shared/ConfirmDialog";

const TiendaPremios = () => {
    const [premios, setPremios] = useState([]);
    const [todosPremios, setTodosPremios] = useState([]); // Todos los premios para el historial
    const [premiosComprados, setPremiosComprados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [idEstudiante, setIdEstudiante] = useState(null);
    const [monedasDisponibles, setMonedasDisponibles] = useState(0);
    const [comprando, setComprando] = useState(null);
    const [tab, setTab] = useState("tienda"); // "tienda" o "historial"
    const [searchTerm, setSearchTerm] = useState("");

    // Estados para modales
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    // 1. Cargar datos de la tienda optimizados
    useEffect(() => {
        const idUsuario = getSessionUserId();
        if (!idUsuario) {
            setError("No se pudo identificar al usuario en sesión.");
            return;
        }

        const loadContent = async () => {
            setLoading(true);
            setError(null);
            try {
                // Obtenemos Todo en una sola transacción: Saldo, ID Estudiante y Catálogo procesado
                // PATCH: Traemos también el estudiante directo porque el SP de tiendaData a veces devuelve 0 monedas
                const [resTienda, resEstudiante] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.tiendaData(idUsuario)),
                    axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuario)).catch(() => ({ data: null }))
                ]);

                const data = resTienda.data || {};
                const studentData = resEstudiante.data ? Models.estudiante(resEstudiante.data) : null;

                // Fallbacks para serialización PascalCase desde .NET
                const rawPremios = data.Premios || data.premios || [];
                const premiosNormalizados = rawPremios.map(rp => ({
                    idPremio: rp.IdPremio !== undefined ? rp.IdPremio : rp.idPremio,
                    idEstudiantePremios: rp.IdEstudiantePremios !== undefined ? rp.IdEstudiantePremios : rp.idEstudiantePremios,
                    nombre: rp.Nombre !== undefined ? rp.Nombre : rp.nombre,
                    descripcion: rp.Descripcion !== undefined ? rp.Descripcion : rp.descripcion,
                    costo: rp.Costo !== undefined ? rp.Costo : rp.costo,
                    rutaURL: rp.RutaURL !== undefined ? rp.RutaURL : rp.rutaURL,
                    fechaIniVigencia: rp.FechaIniVigencia !== undefined ? rp.FechaIniVigencia : rp.fechaIniVigencia,
                    fechaFinVigencia: rp.FechaFinVigencia !== undefined ? rp.FechaFinVigencia : rp.fechaFinVigencia,
                    comprado: rp.Comprado !== undefined ? rp.Comprado : rp.comprado,
                    fechaCompra: rp.FechaCompra !== undefined ? rp.FechaCompra : rp.fechaCompra
                }));

                const dataIdEstudiante = data.IdEstudiante !== undefined ? data.IdEstudiante : data.idEstudiante;
                const dataMonedas = data.MonedasDisponibles !== undefined ? data.MonedasDisponibles : data.monedasDisponibles;

                // Prioridad al ID y Monedas del endpoint directo de estudiante si existe
                const realIdEstudiante = studentData?.idEstudiante || dataIdEstudiante;
                const realMonedas = studentData ? (studentData.modenas ?? studentData.monedas ?? 0) : dataMonedas;

                setIdEstudiante(realIdEstudiante);
                setMonedasDisponibles(realMonedas);
                setTodosPremios(premiosNormalizados);

                // Filtrar para la tienda: Vigentes y NO comprados
                const hoy = new Date();
                const disponibles = premiosNormalizados.filter(p => {
                    if (p.comprado) return false;
                    
                    if (p.fechaIniVigencia) {
                        const startDate = new Date(p.fechaIniVigencia.split('T')[0] + "T00:00:00");
                        if (hoy < startDate) return false;
                    }
                    
                    if (p.fechaFinVigencia) {
                        const endDate = new Date(p.fechaFinVigencia.split('T')[0] + "T23:59:59");
                        if (hoy > endDate) return false;
                    }
                    
                    return true;
                });
                setPremios(disponibles);

                // Filtrar para el historial: Solo comprados
                setPremiosComprados(premiosNormalizados.filter(p => p.comprado));

                console.log('[TiendaPremios] Datos cargados (con patch de monedas)');
            } catch (err) {
                console.error("Error al cargar tienda:", err);
                setError("Ocurrió un error al cargar los premios.");
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, []);

    const handleComprarPremio = async (premio) => {
        if (!idEstudiante) return;

        // Verificar monedas suficientes
        if (monedasDisponibles < premio.costo) {
            setAlertDialog({
                open: true,
                title: 'Monedas insuficientes',
                message: (
                    <div>
                        <p>No tienes suficientes monedas para comprar este premio.</p>
                        <br />
                        <p><strong>Coste:</strong> {premio.costo} monedas</p>
                        <p><strong>Tienes:</strong> {monedasDisponibles} monedas</p>
                    </div>
                ),
                variant: 'warning'
            });
            return;
        }

        // Mostrar confirmación
        setConfirmDialog({
            open: true,
            title: 'Confirmar compra',
            message: `¿Deseas canjear "${premio.nombre}" por ${premio.costo} monedas?`,
            onConfirm: async () => {
                await proceedWithPurchase(premio);
            }
        });
    };

    const proceedWithPurchase = async (premio) => {
        setComprando(premio.idPremio);
        try {
            // Un solo POST que hace todo en el servidor (Transacción SQL)
            const res = await axios.post(LMS_ENDPOINTS.tiendaComprar(idEstudiante, premio.idPremio));

            if (res.data) {
                const nuevasMonedas = monedasDisponibles - premio.costo;
                setMonedasDisponibles(nuevasMonedas);

                // Actualizar listas locales sin recargar todo
                const updatedPremio = { ...premio, comprado: true, fechaCompra: new Date().toISOString() };

                setPremios(prev => prev.filter(p => p.idPremio !== premio.idPremio));
                setPremiosComprados(prev => [updatedPremio, ...prev]);
                setTodosPremios(prev => prev.map(p => p.idPremio === premio.idPremio ? updatedPremio : p));

                setAlertDialog({
                    open: true,
                    title: '¡Éxito!',
                    message: `Has adquirido "${premio.nombre}". Tu nuevo saldo es ${nuevasMonedas} monedas.`,
                    variant: 'success'
                });
            } else {
                throw new Error("Transacción fallida");
            }
        } catch (error) {
            console.error("Error al comprar:", error);
            setAlertDialog({ open: true, title: 'Error', message: 'No se pudo completar la compra.', variant: 'error' });
        } finally {
            setComprando(null);
        }
    };

    const isPremioComprado = (idPremio) => {
        return premiosComprados.some(
            (comp) => String(comp.idPremio) === String(idPremio)
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("es-MX", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    };

    return (
        <div className="panel-sigma-main">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <div className="profile-header-title" style={{ marginBottom: 0 }}>
                        <h1 style={{ margin: 0 }}>Tienda de Premios</h1>
                        <p style={{ color: "#64748b", marginTop: "0.5rem", marginBottom: 0 }}>
                            Canjea tus monedas por premios increíbles
                        </p>
                    </div>
                </div>

                <div style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    backgroundColor: '#ffffff',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '16px',
                    border: '1px solid #f1f5f9',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                    <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '10px',
                        backgroundColor: 'var(--color-primary)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        boxShadow: '0 4px 6px -1px var(--color-primary-light, rgba(0,0,0,0.2))'
                    }}>
                        <FiDollarSign />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                            Tus Monedas
                        </span>
                        <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', lineHeight: '1', marginTop: '0.1rem' }}>
                            {monedasDisponibles !== null && monedasDisponibles !== undefined ? monedasDisponibles : 0}
                        </span>
                    </div>
                </div>
            </div>

            {/* Tabs y Buscador en la misma fila */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                gap: '1.5rem',
                flexWrap: 'wrap'
            }}>
                {/* Tabs Style Switcher - Segmented Control */}
                <div style={{
                    display: 'flex',
                    flex: '1 1 400px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '12px',
                    padding: '0.4rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                }}>
                    <div
                        onClick={() => setTab("tienda")}
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '0.4rem 0',
                            fontSize: '0.9rem',
                            fontWeight: tab === 'tienda' ? 700 : 500,
                            color: tab === 'tienda' ? 'var(--color-primary-dark)' : '#64748b',
                            backgroundColor: tab === 'tienda' ? '#ffffff' : 'transparent',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            userSelect: 'none',
                            boxShadow: tab === 'tienda' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        🛍️ Tienda
                    </div>
                    <div
                        onClick={() => setTab("historial")}
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '0.4rem 0',
                            fontSize: '0.9rem',
                            fontWeight: tab === 'historial' ? 700 : 500,
                            color: tab === 'historial' ? 'var(--color-primary-dark)' : '#64748b',
                            backgroundColor: tab === 'historial' ? '#ffffff' : 'transparent',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            userSelect: 'none',
                            boxShadow: tab === 'historial' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        📜 Historial de Compras
                    </div>
                </div>

                {/* Buscador alineado a la derecha */}
                <div style={{ position: 'relative', flex: '0 1 350px', width: '100%' }}>
                    <FiSearch style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#94a3b8',
                        fontSize: '1.1rem'
                    }} />
                    <input
                        type="text"
                        placeholder={tab === 'tienda' ? "Buscar en la tienda..." : "Buscar en mi historial..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.75rem 1rem 0.75rem 2.8rem',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            fontSize: '0.95rem',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                            color: '#1e293b'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm("")}
                            style={{
                                position: 'absolute',
                                right: '0.8rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: '#f1f5f9',
                                border: 'none',
                                color: '#64748b',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '0.65rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Contenido principal */}
            {loading ? (
                <div className="catalog-loading">Cargando premios...</div>
            ) : error ? (
                <div
                    style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#ef4444",
                        backgroundColor: "#fef2f2",
                        borderRadius: "8px",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            ) : tab === "tienda" ? (
                // TAB: TIENDA - Premios disponibles para comprar
                premios.filter(p => 
                    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    p.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 ? (
                    <div
                        style={{
                            padding: "3rem",
                            textAlign: "center",
                            color: "#94a3b8",
                            backgroundColor: "#f8fafc",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <FiGift size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} />
                        <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                            {searchTerm ? "No se encontraron premios que coincidan con tu búsqueda" : "No hay premios disponibles en este momento"}
                        </p>
                        <p style={{ fontSize: "0.9rem" }}>
                            {searchTerm ? "Intenta con otro término" : "Vuelve más tarde para ver nuevos premios"}
                        </p>
                    </div>
                ) : (
                    <div className="premios-grid">
                        {premios
                            .filter(p => 
                                p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                p.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map((premio) => {
                            const comprado = isPremioComprado(premio.idPremio);
                            const puedeComprar = monedasDisponibles >= premio.costo && !comprado;
                            const comprando_este = comprando === premio.idPremio;

                            return (
                                <article
                                    key={premio.idPremio}
                                    className={`premio-card ${comprado ? "premio-card--comprado" : ""}`}
                                >
                                    <div className="premio-header">
                                        {premio.rutaURL ? (
                                            <div className="premio-image">
                                                <img
                                                    src={premio.rutaURL.startsWith('http') ? premio.rutaURL : `${SERVER_URL}${premio.rutaURL}`}
                                                    alt={premio.nombre || "Imagen del premio"}
                                                />
                                            </div>
                                        ) : (
                                            <div className="premio-image premio-image-placeholder">
                                                <FiGift size={48} />
                                            </div>
                                        )}
                                        {comprado && (
                                            <div className="premio-badge-comprado">
                                                <FiCheck size={16} />
                                                <span>Comprado</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="premio-body">
                                        <h3 className="premio-title">{premio.nombre}</h3>
                                        <p className="premio-description">
                                            {premio.descripcion || "Sin descripción"}
                                        </p>

                                        <div className="premio-meta">
                                            <div className="premio-costo">
                                                <FiDollarSign size={18} />
                                                <span className="premio-costo-valor">
                                                    {premio.costo}
                                                </span>
                                                <span className="premio-costo-label">monedas</span>
                                            </div>

                                            {premio.fechaFinVigencia && (
                                                <div className="premio-vigencia">
                                                    <FiClock size={14} />
                                                    <span>Válido hasta {formatDate(premio.fechaFinVigencia)}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="premio-actions">
                                            {comprado ? (
                                                <button
                                                    type="button"
                                                    className="premio-btn premio-btn-comprado"
                                                    disabled
                                                >
                                                    <FiCheck size={16} />
                                                    Ya lo tienes
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="premio-btn premio-btn-comprar"
                                                    onClick={() => handleComprarPremio(premio)}
                                                    disabled={!puedeComprar || comprando_este}
                                                >
                                                    {comprando_este ? (
                                                        <>Procesando...</>
                                                    ) : !puedeComprar ? (
                                                        <>
                                                            <FiShoppingCart size={16} />
                                                            Monedas insuficientes
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiShoppingCart size={16} />
                                                            Comprar
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )
            ) : (
                // TAB: HISTORIAL - Premios comprados
                premiosComprados.filter(p => {
                    const premio = todosPremios.find(tp => String(tp.idPremio) === String(p.idPremio));
                    return premio?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           premio?.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
                }).length === 0 ? (
                    <div
                        style={{
                            padding: "3rem",
                            textAlign: "center",
                            color: "#94a3b8",
                            backgroundColor: "#f8fafc",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <FiShoppingCart size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} />
                        <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                            {searchTerm ? "No se encontraron compras que coincidan con tu búsqueda" : "Aún no has comprado ningún premio"}
                        </p>
                        <p style={{ fontSize: "0.9rem" }}>
                            {searchTerm ? "Intenta con otro término" : "Explora la tienda y canjea tus monedas"}
                        </p>
                    </div>
                ) : (
                    <div className="historial-grid">
                        {premiosComprados
                            .filter(p => {
                                const premio = todosPremios.find(tp => String(tp.idPremio) === String(p.idPremio));
                                return premio?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                       premio?.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
                            })
                            .map((compra) => {
                            const premio = todosPremios.find(
                                (p) => String(p.idPremio) === String(compra.idPremio)
                            );

                            return (
                                <article key={compra.idEstudiantePremios || `compra-${compra.idPremio}-${compra.fechaCompra}`} className="historial-card">
                                    <div className="historial-image">
                                        {premio?.rutaURL ? (
                                            <img
                                                src={premio.rutaURL.startsWith('http') ? premio.rutaURL : `${SERVER_URL}${premio.rutaURL}`}
                                                alt={premio.nombre || "Imagen del premio"}
                                            />
                                        ) : (
                                            <div className="historial-image-placeholder">
                                                <FiGift size={32} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="historial-info">
                                        <h4 className="historial-nombre">
                                            {premio?.nombre || `Premio #${compra.idPremio}`}
                                        </h4>
                                        <p className="historial-descripcion">
                                            {premio?.descripcion || "Sin descripción"}
                                        </p>
                                        <div className="historial-detalles">
                                            <div className="historial-detalle">
                                                <FiDollarSign size={14} />
                                                <span>{compra.costo} monedas</span>
                                            </div>
                                            <div className="historial-detalle">
                                                <FiClock size={14} />
                                                <span>Comprado: {formatDate(compra.fechaCompra)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="historial-badge">
                                        <FiCheck size={16} />
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )
            )}

            <style jsx="true">{`
        .premios-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
        }

        /* Pill Tabs Style Removed (Using Inline) */

        .premio-card {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
        }

        .premio-card:hover {
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
          transform: translateY(-3px);
        }

        .premio-card--comprado {
          opacity: 0.8;
          border-color: #10b981;
        }

        .premio-header {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* Aspect ratio 16:9 */
          overflow: hidden;
          background: var(--btn-primary);
        }

        .premio-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .premio-image img {
          width: 100%;
          height: 100%;
          object-fit: fill;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .premio-image-placeholder {
          color: white;
          opacity: 0.8;
        }

        .premio-badge-comprado {
          position: absolute;
          top: 0.6rem;
          right: 0.6rem;
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.7rem;
          background: #10b981;
          color: white;
          border-radius: 14px;
          font-size: 0.75rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          z-index: 10;
        }

        .premio-body {
          padding: 1rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .premio-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.35rem 0;
          line-height: 1.3;
        }

        .premio-description {
          font-size: 0.8rem;
          color: #64748b;
          margin: 0 0 0.75rem 0;
          line-height: 1.4;
          min-height: 2.2rem;
          flex: 1;
        }

        .premio-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e2e8f0;
        }

        .premio-costo {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          color: var(--color-primary);
        }

        .premio-costo-valor {
          font-size: 1.25rem;
          font-weight: 700;
        }

        .premio-costo-label {
          font-size: 0.8rem;
          color: #64748b;
        }

        .premio-vigencia {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.75rem;
          color: #64748b;
        }

        .premio-actions {
          display: flex;
          gap: 0.5rem;
        }

        .premio-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.6rem 0.75rem;
          border: none;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .premio-btn-comprar {
          background: var(--btn-primary);
          color: white;
        }

        .premio-btn-comprar:hover:not(:disabled) {
          transform: translateY(-2px);
          background: var(--btn-primary-600);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .premio-btn-comprar:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #94a3b8;
        }

        .premio-btn-comprado {
          background: #10b981;
          color: white;
          cursor: default;
        }

        /* Estilos para Historial */
        .historial-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
        }

        .historial-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          display: flex;
          gap: 1.5rem;
          align-items: center;
          transition: all 0.2s ease;
          position: relative;
        }

        .historial-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .historial-image {
          width: 100px;
          height: 100px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          background: linear-gradient(135deg, #c92a2a 0%, #a61e1e 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .historial-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .historial-image-placeholder {
          color: white;
          opacity: 0.8;
        }

        .historial-info {
          flex: 1;
          min-width: 0;
        }

        .historial-nombre {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
        }

        .historial-descripcion {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0 0 1rem 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .historial-detalles {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .historial-detalle {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #64748b;
        }

        .historial-badge {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #10b981;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .premios-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }

          .historial-grid {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .historial-card {
            padding: 1rem;
          }

          .historial-image {
            width: 80px;
            height: 80px;
          }

          .historial-nombre {
            font-size: 1rem;
          }

          .historial-descripcion {
            font-size: 0.8rem;
          }

          .historial-detalle {
            font-size: 0.75rem;
          }

          .historial-badge {
            width: 36px;
            height: 36px;
          }
        }
      `}</style>

            {/* Modales */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />

            <ConfirmDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
            />
        </div>
    );
};

export default TiendaPremios;
